import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Search, AlertTriangle, Copy, ExternalLink, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import StatusChip from "@/components/StatusChip";
import GroupBillModal from "@/components/GroupBillModal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatAmount, formatDate, asText } from "@/lib/format";

const TABS = [
  { key: "NEEDS_REVIEW", label: "Needs Review" },
  { key: "NEEDS_OCR_REVIEW", label: "Needs OCR Review" },
  { key: "BLOCKED", label: "Blocked" },
  { key: "OCR_FAILED", label: "OCR Failed" },
  { key: "REJECTED", label: "Rejected" },
];

export default function CandidateQueue() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const batchId = params.get("batch_id") || "";
  const tab = params.get("tab") || "NEEDS_REVIEW";
  const [q, setQ] = useState(params.get("q") || "");
  const [counts, setCounts] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Multi-select for Phase 1 manual grouping. Only image-based
  // non-terminal candidates in the same batch can be grouped, so we
  // gate the eligibility check per-row rather than trying to enforce
  // it via the checkbox alone (server also re-validates).
  const [selected, setSelected] = useState(() => new Set());
  const [groupOpen, setGroupOpen] = useState(false);
  const isGroupable = (c) => {
    const terminal = new Set([
      "SENT_TO_EC", "CONFIRMED_LINKED", "CONFIRMED_IN_BILLS", "SENDING_TO_EC", "CONFIRMING",
      "REJECTED", "GROUPED_INTO",
    ]);
    if (terminal.has(c.status)) return false;
    if ((c.attachment_content_type || "").startsWith("application/pdf")) return false;
    // Already a grouped bill.
    if (c.grouping_mode === "MANUAL") return false;
    return true;
  };
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const selectedRows = useMemo(
    () => rows.filter((c) => selected.has(c.id)),
    [rows, selected]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.candidateCounts(batchId),
        api.listCandidates({ status: tab, batch_id: batchId || undefined, q: q || undefined }),
      ]);
      setCounts(c);
      setRows(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, batchId, q]);

  const switchTab = (k) => {
    const next = new URLSearchParams(params);
    next.set("tab", k);
    setParams(next, { replace: true });
  };

  const total = useMemo(
    () =>
      Object.entries(counts)
        .filter(([k]) => k !== "NEEDS_OCR_REVIEW")
        .reduce((a, [, v]) => a + (v || 0), 0),
    [counts]
  );

  // Build a candidate-review URL that carries the operator's active
  // queue context (tab, batch, search). CandidateReview reads these
  // params to power auto-advance after Send to EC / Reject. URL params
  // are used instead of navigation state so a refresh doesn't drop the
  // queue cursor.
  const reviewHref = (id) => {
    const qs = new URLSearchParams();
    if (tab) qs.set("queue_status", tab);
    if (batchId) qs.set("batch_id", batchId);
    if (q) qs.set("q", q);
    const s = qs.toString();
    return `/candidates/${id}${s ? `?${s}` : ""}`;
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-[28px] font-semibold text-archival-primary leading-tight tracking-tight">
            Review
          </h1>
          <p className="text-[14px] text-archival-secondary mt-1">
            Captured bills waiting for a human decision · {total} total.{" "}
            {batchId && (
              <span className="font-mono text-[12px] text-archival-muted">
                · batch {batchId.slice(0, 8)}{" "}
                <Link to="/queue" className="underline">clear</Link>
              </span>
            )}
          </p>
        </div>
        <div className="relative w-full md:w-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-archival-muted" />
          <Input
            data-testid={TID.queueSearch}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search vendor, bill number, file…"
            className="pl-9 w-full md:w-[320px] border-archival bg-white"
          />
        </div>
      </header>

      <div className="border-b border-archival flex gap-1 px-1 overflow-x-auto no-scrollbar">
        {TABS.map((t) => {
          const c = counts[t.key] || 0;
          const isActive = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={TID.queueTab(t.key)}
              onClick={() => switchTab(t.key)}
              className={`py-2.5 px-3 text-[13px] font-medium border-b-2 transition-all ${
                isActive
                  ? "border-action text-archival-primary"
                  : "border-transparent text-archival-secondary hover:text-archival-primary"
              }`}
              style={isActive ? { borderColor: "var(--action)" } : undefined}
            >
              {t.label}
              <span
                className={`ml-1.5 inline-flex items-center justify-center min-w-[20px] px-1.5 h-[18px] rounded-full text-[10px] font-semibold ${
                  isActive ? "bg-archival-soft text-archival-primary" : "bg-archival-soft text-archival-muted"
                }`}
              >
                {c}
              </span>
            </button>
          );
        })}
      </div>

      {/* Multi-select action bar — shows once ≥2 groupable rows are
          selected. Bulk Capture is a queue-processing console; grouping
          images that belong to the same physical bill is a common
          operator flow. */}
      {selected.size > 0 && (
        <div
          data-testid="queue-multiselect-bar"
          className="flex items-center justify-between px-3 py-2 rounded-sm bg-archival-primary text-white text-[12px]"
        >
          <div className="flex items-center gap-2">
            <Layers size={13} />
            <span data-testid="queue-multiselect-count">
              {selected.size} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelection}
              data-testid="queue-multiselect-clear"
              className="h-7 text-[11px] bg-transparent border-white/40 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => setGroupOpen(true)}
              disabled={selected.size < 2}
              data-testid="queue-multiselect-group"
              className="h-7 text-[11px] bg-white text-archival-primary hover:bg-white/90 disabled:opacity-40"
            >
              Group as Same Bill
            </Button>
          </div>
        </div>
      )}

      <GroupBillModal
        open={groupOpen}
        candidates={selectedRows}
        onClose={() => setGroupOpen(false)}
        onGrouped={(r) => {
          clearSelection();
          load();
          // Optional: navigate straight into the new grouped bill so
          // the operator lands on the review screen after grouping.
          navigate(reviewHref(r.candidate_id));
        }}
      />

      <div className="rounded-md border border-archival bg-archival-surface overflow-x-auto">
        {loading && rows.length === 0 ? (
          <div className="py-16 text-center text-archival-muted text-[13px]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-archival-muted text-[13px]">
            {tab === "NEEDS_REVIEW"
              ? "No bills waiting for review."
              : tab === "NEEDS_OCR_REVIEW"
              ? "No candidates with missing OCR details."
              : tab === "BLOCKED"
              ? "No blocked bills."
              : tab === "OCR_FAILED"
              ? "No OCR failures."
              : "Nothing here."}
          </div>
        ) : (
          <>
          <ul className="hidden md:block">
            {rows.map((c) => (
              <li
                key={c.id}
                data-testid={TID.queueRow(c.id)}
                className="grid grid-cols-[24px_repeat(12,minmax(0,1fr))] min-w-[820px] items-center gap-3 px-4 h-[72px] border-b border-archival last:border-b-0 hover:bg-archival-soft transition-colors cursor-pointer"
                onClick={() => navigate(reviewHref(c.id))}
              >
                <div
                  className="flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  {isGroupable(c) ? (
                    <input
                      type="checkbox"
                      checked={selected.has(c.id)}
                      onChange={() => toggleSelect(c.id)}
                      data-testid={`queue-select-${c.id}`}
                      className="h-3.5 w-3.5 accent-archival-primary cursor-pointer"
                      title="Select for grouping"
                    />
                  ) : (
                    <span className="inline-block h-3.5 w-3.5" />
                  )}
                </div>
                <div className="col-span-3 min-w-0">
                  <div className="text-[14px] font-semibold text-archival-primary truncate">
                    {c.suggested_vendor_name || c.extracted_vendor_name || (
                      <span className="text-archival-muted italic font-normal">Vendor unresolved</span>
                    )}
                  </div>
                  <div className="text-[11px] text-archival-muted truncate flex items-center gap-1.5">
                    {c.grouping_mode === "MANUAL" && (c.page_count || 0) > 1 && (
                      <span
                        data-testid={`queue-row-pagecount-${c.id}`}
                        className="inline-flex items-center gap-1 px-1 py-0.5 rounded-sm bg-archival-soft border border-archival text-[10px] font-mono uppercase tracking-wider text-archival-secondary"
                      >
                        <Layers size={9} /> {c.page_count}-page bill
                      </span>
                    )}
                    <span className="truncate">{c.original_filename}</span>
                  </div>
                </div>
                <div className="col-span-2 text-[13px] font-mono text-archival-primary">
                  {c.bill_number || <span className="text-archival-muted">—</span>}
                </div>
                <div className="col-span-2 text-[13px] font-mono text-archival-secondary">
                  {formatDate(c.bill_date)}
                </div>
                <div className="col-span-2 text-[14px] font-mono font-semibold text-archival-primary">
                  {formatAmount(c.total_amount, c.currency)}
                </div>
                <div className="col-span-2 flex flex-col gap-1 items-start">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <StatusChip status={c.status} />
                    {c.duplicate_state === "HARD_DUPLICATE" && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-red-700">
                        <Copy size={10} /> dup
                      </span>
                    )}
                    {c.duplicate_state === "POSSIBLE_DUPLICATE" && (
                      <span className="inline-flex items-center gap-1 text-[10px] text-amber-700">
                        <AlertTriangle size={10} /> dup?
                      </span>
                    )}
                  </div>
                  {c.error_message && (
                    <div className="text-[10px] text-red-700 truncate max-w-full" title={asText(c.error_message)}>
                      {asText(c.error_message)}
                    </div>
                  )}
                  {c.duplicate_state === "HARD_DUPLICATE" && c.duplicate_existing_bill && (
                    <div
                      data-testid={`queue-dup-info-${c.id}`}
                      className="text-[10px] text-red-800 max-w-full truncate"
                      title={`Duplicate of bill #${c.duplicate_existing_bill.bill_number || "—"}${c.duplicate_existing_bill.vendor_name ? " · " + c.duplicate_existing_bill.vendor_name : ""}${c.duplicate_existing_bill.bill_date ? " · " + c.duplicate_existing_bill.bill_date : ""}`}
                    >
                      Duplicate of bill #{c.duplicate_existing_bill.bill_number || "—"}
                      {c.duplicate_existing_bill.vendor_name ? ` · ${c.duplicate_existing_bill.vendor_name}` : ""}
                      {c.duplicate_existing_bill.bill_date ? ` · ${c.duplicate_existing_bill.bill_date}` : ""}
                    </div>
                  )}
                  {c.duplicate_state === "HARD_DUPLICATE" && c.duplicate_existing_bill?.url && (
                    <a
                      href={c.duplicate_existing_bill.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`queue-dup-open-${c.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 hover:text-red-900 underline"
                    >
                      Open existing bill <ExternalLink size={9} />
                    </a>
                  )}
                </div>
                <div className="col-span-1 text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={TID.queueReviewBtn(c.id)}
                    className="border-archival bg-white text-[12px] h-7"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(reviewHref(c.id));
                    }}
                  >
                    Review
                  </Button>
                </div>
              </li>
            ))}
          </ul>

          {/* Phones get decision cards: vendor, bill number, date, amount, state, one action. */}
          <ul className="md:hidden divide-y divide-archival" data-testid="queue-mobile-cards">
            {rows.map((c) => (
              <li key={`m-${c.id}`} data-testid={`queue-card-${c.id}`} className="p-3">
                <Link to={reviewHref(c.id)} className="block">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[15px] font-semibold text-archival-primary truncate">
                        {c.suggested_vendor_name || c.extracted_vendor_name || "Vendor not set"}
                      </p>
                      <p className="text-[12px] text-archival-secondary truncate">
                        {c.bill_number || "No bill number"}{c.bill_date ? ` · ${c.bill_date}` : ""}
                      </p>
                    </div>
                    <p className="shrink-0 text-[15px] font-semibold tabular-nums text-archival-primary">
                      {(c.total_amount ?? c.bill_total) != null
                        ? `${c.currency === "INR" || !c.currency ? "₹" : `${c.currency} `}${Number(c.total_amount ?? c.bill_total).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
                        : "—"}
                    </p>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <StatusChip status={c.status} />
                    <span className="text-[13px] font-medium" style={{ color: "var(--action)" }}>
                      Review →
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          </>
        )}
      </div>

      {(counts.SENT_TO_EC || counts.SEND_FAILED || counts.READY_TO_SEND) ? (
        <p className="text-[12px] text-archival-muted" data-testid="queue-legacy-note">
          Bills handed to Expense Capture before 1OS Bills became canonical are kept as history ·{" "}
          <Link to="/legacy/expense-capture" className="underline" data-testid="queue-legacy-link">
            Legacy · Sent to Expense Capture ({(counts.SENT_TO_EC || 0) + (counts.SEND_FAILED || 0) + (counts.READY_TO_SEND || 0)})
          </Link>
        </p>
      ) : null}
    </div>
  );
}
